import { useMutation, useQuery } from '@tanstack/react-query';
import {
  compareAgentTemplateVersionsMutation,
  getAgentTemplateVersionComparisonOptions,
} from '../generated/api/@tanstack/react-query.gen.js';
import { adaptMutation } from './adapt-mutation.js';

/**
 * Trigger a multi-version comparison job.
 * Returns a mutation whose `.data` is `AgentTemplateVersionCompareResult`:
 *   - status === 'queued'     → compareJobId is set, poll via useVersionComparison
 *   - status === 'no_dataset' → compareJobId is null, inform the user
 */
export function useCompareVersions(templateId: string) {
  return useMutation(
    adaptMutation(
      compareAgentTemplateVersionsMutation(),
      ({ versionIds, samples }: { versionIds: number[]; samples?: number }) => ({
        path: { templateId },
        body: { versionIds, samples },
      }),
    ),
  );
}

/**
 * Poll an in-progress comparison until `.complete === true`.
 * Polling stops automatically once the response is complete (refetchInterval → false).
 */
export function useVersionComparison(
  templateId: string,
  compareJobId: string | null,
  versionIds: number[],
  samples: number | undefined,
) {
  const query = useQuery({
    ...getAgentTemplateVersionComparisonOptions({
      path: { templateId, compareJobId: compareJobId ?? '' },
      query: { versionIds: versionIds.join(','), samples },
    }),
    enabled: !!compareJobId,
    refetchInterval: (q) => {
      if (q.state.data?.complete) return false;
      return 4000;
    },
  });
  if (query.isError) console.error('useVersionComparison: failed to load version comparison', query.error);
  return query;
}
